import { IAugmentedJQuery, IDirective, IDirectiveFactory, IAttributes, INgModelController, IScope, ITimeoutService } from "angular";

import { ItemEvents } from "@gtmhub/customFields";
import { blurFocusedElement } from "@gtmhub/shared/dom";
import { manualScrollSuggestionsDropdown } from "@gtmhub/shared/mentions";
import { IMention } from "@gtmhub/comments";
interface EditableTextareaScope extends IScope {
  markdown?: boolean;
  mentions?: boolean;
  disabled?: boolean;
  required?: boolean;
  inline?: boolean;
  instantUpdate?: boolean;
  submitEnter?: boolean;
  autoFocus?: boolean;
  preventBlurOnSubmit?: boolean;
  isTextareaVisible?: boolean;
  placeholder?: string;
  useMarkdownPlaceholderOnBlur?: boolean;
  fieldName?: string;
  model: {
    newText?: string;
  };
  suggestionsDropdown: {
    isOpen: boolean;
  };
  toggleTextarea(): void;
  isTooltipEnabled(): boolean;
  changeText(): void;
  onSubmit(): void;
  onKeyDown($event: KeyboardEvent): void;
  onMentionsChanged({ mentions }: { mentions: IMention[] | string[] }): void;
}

export class EditableTextareaDirective implements IDirective {
  scope = {
    markdown: "<",
    mentions: "<",
    submitEnter: "<",
    autoFocus: "<",
    preventBlurOnSubmit: "<",
    inline: "<",
    instantUpdate: "<",
    fieldName: "@",
    placeholder: "@",
    useMarkdownPlaceholderOnBlur: "<?",
    onMentionsChanged: "&",
    disabled: "=ngDisabled",
    required: "=ngRequired",
    onFocus: "&",
    onBlur: "&",
  };
  restrict = "E";
  require = "ngModel";
  templateUrl = require("./editable-textarea.jade");

  constructor(private $timeout: ITimeoutService) {}

  link(scope: EditableTextareaScope, element: IAugmentedJQuery, attrs: IAttributes, ctrl: INgModelController): void {
    const itemEvents = new ItemEvents(scope);

    const updateView = (value: string) => {
      scope.model = { newText: value };
    };

    itemEvents.onItemUpdated((args) => {
      const value = args.update.customFields[scope.fieldName];
      value && updateView(value);
    });

    ctrl.$render = () => updateView(ctrl.$modelValue);

    scope.suggestionsDropdown = {
      isOpen: false,
    };

    scope.isTextareaVisible = !scope.markdown;

    if (scope.instantUpdate) {
      scope.$watch("model.newText", (newValue) => {
        ctrl.$setViewValue(newValue || "");
      });
    }

    scope.toggleTextarea = () => {
      if (scope.disabled) {
        return;
      }

      scope.isTextareaVisible = !scope.isTextareaVisible;
      if (scope.isTextareaVisible) {
        // ui-mention dynamically calculate the height of the textarea,
        // so we want to remove this height after ui-mention calculate the height
        // if the element is inline then we want to remove the height every time
        this.focusElementAndRemoveHeight(element, scope, scope.inline);
      }
    };

    if (scope.autoFocus) {
      if (scope.markdown) {
        scope.toggleTextarea();
      } else {
        this.focusElementAndRemoveHeight(element, scope, false);
      }
    }

    scope.changeText = () => {
      if (scope.markdown) {
        // We need this timeout as HTML blur event is fired first and
        // other click events might not get fired due to HTML DOM changes.
        this.$timeout(() => {
          scope.toggleTextarea();
        }, 200);
      }

      if (scope.required && !scope.model.newText) {
        scope.model = { newText: ctrl.$viewValue };
        return;
      }

      if (scope.model.newText !== ctrl.$viewValue) {
        ctrl.$setViewValue(scope.model.newText);
      }
    };

    scope.onSubmit = () => {
      if (scope.preventBlurOnSubmit) {
        return;
      }

      setTimeout(blurFocusedElement);
    };

    scope.onKeyDown = ($event: KeyboardEvent) => {
      manualScrollSuggestionsDropdown({ $event, isSuggestionsDropdownOpen: scope.suggestionsDropdown.isOpen });

      if ($event.key === "Enter" && scope.submitEnter) {
        // To not lose focus on textarea/input if you select mention with Enter
        if (scope.suggestionsDropdown.isOpen) {
          scope.suggestionsDropdown.isOpen = false;
          return;
        }

        $event.preventDefault();
        $event.stopPropagation();
        setTimeout(() => ($event.target as HTMLElement).blur());
      }
    };

    scope.isTooltipEnabled = (): boolean => {
      const foundElement = element[0].querySelector("input");
      if (!foundElement) {
        return false;
      }

      const foundElementStyles = window.getComputedStyle(foundElement);
      const elWidth = foundElement.clientWidth;
      const elMaxWidth = foundElementStyles.maxWidth || "0px";
      const elMaxWidthNumber = Number(elMaxWidth.slice(0, elMaxWidth.length - 2));
      const borderSize = 2;

      return elWidth + borderSize === elMaxWidthNumber;
    };
  }

  static factory(): IDirectiveFactory {
    const factory = ($timeout: ITimeoutService) => new EditableTextareaDirective($timeout);
    factory.$inject = ["$timeout"];
    return factory;
  }

  private focusElementAndRemoveHeight(element: IAugmentedJQuery, scope: EditableTextareaScope, removeHeight: boolean) {
    this.$timeout(() => {
      const elementFind = scope.inline ? "input" : "textarea";
      const foundElement = element[0].querySelector<HTMLElement>(elementFind);
      foundElement && foundElement.focus();
      if (foundElement && removeHeight) {
        foundElement.style.height = "";
      }
    }, 40);
  }
}
